/**
 * Category Management Component
 * Allows admins to manage tour categories
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useCategoryOrder } from '../../contexts/CategoryOrderContext';
import { categoryService, Category } from '../../services/categoryService';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckIcon,
  XMarkIcon,
  TagIcon
} from '@heroicons/react/24/outline';

const CategoryManagement: React.FC = () => {
  const { actualTheme } = useTheme();
  const { featuredCategories } = useCategoryOrder();
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryImages, setCategoryImages] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#059669',
    icon: '🏷️',
    isActive: true
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadCategories();

    // Load from localStorage if not editing
    const cachedData = localStorage.getItem('category_form_cache');
    if (cachedData && !editingCategory) {
      try {
        const parsed = JSON.parse(cachedData);
        setFormData(prev => ({ ...prev, ...parsed }));
        // console.log('📦 CategoryManagement: Recovered unsaved data from cache');
        setShowAddForm(true);
      } catch (e) {
        console.error('Error parsing category form cache:', e);
      }
    }
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      // console.log('🔄 CategoryManagement: Force refreshing categories...');
      // Force refresh to get latest categories from backend
      const allCategories = await categoryService.getAllCategories(true);
      // console.log(`✅ CategoryManagement: Loaded ${allCategories.length} categories`);

      // Apply featured category order
      const orderedCategories = applyFeaturedCategoryOrder(allCategories, featuredCategories);
      setCategories(orderedCategories);

      // Load cover images from first package of each category
      await loadCategoryCoverImages(orderedCategories);
    } catch (error) {
      console.error('Error loading categories:', error);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to apply featured category order
  const applyFeaturedCategoryOrder = (categories: Category[], featuredOrder: Category[]): Category[] => {
    if (!featuredOrder || featuredOrder.length === 0) {
      return categories;
    }

    const categoryMap = new Map(categories.map(cat => [cat.id, cat]));
    const orderedCategories: Category[] = [];
    const usedIds = new Set<string>();

    // First, add categories in the featured order
    for (const featuredCat of featuredOrder) {
      const category = categoryMap.get(featuredCat.id);
      if (category) {
        orderedCategories.push(category);
        usedIds.add(category.id);
      }
    }

    // Then, add any categories that weren't in the featured order
    for (const category of categories) {
      if (!usedIds.has(category.id)) {
        orderedCategories.push(category);
      }
    }

    // console.log(`📋 CategoryManagement: Ordered ${orderedCategories.length} categories (${featuredOrder.length} featured + ${orderedCategories.length - featuredOrder.length} others)`);
    return orderedCategories;
  };

  const loadCategoryCoverImages = async (categories: Category[]) => {
    const images: Record<string, string> = {};

    // Load images for each category from tours - use first tour based on saved order (consistent with system)
    await Promise.all(
      categories.map(async (category) => {
        try {
          const { tourService } = await import('../../services/tourService');
          // Force refresh tours to get latest data
          const allTours = await tourService.getAllTours(true);

          // Try to match tours by categoryId first, then fallback to category name (consistent with hero slideshow)
          let categoryTours = allTours.filter(tour => {
            // Try categoryId match first
            const tourCategoryId = (tour as any).categoryId;
            if (tourCategoryId && tourCategoryId === category.id) {
              return true;
            }

            // Fallback to category name match (case-insensitive)
            if (tour.category && tour.category.toLowerCase() === category.name.toLowerCase()) {
              return true;
            }

            return false;
          });

          // ENFORCE: Apply featured order with category-specific ordering (this determines which tour is "first")
          // This ensures both global featured order AND category-specific order are respected
          const { applyFeaturedOrderWithCategory } = await import('../../utils/applyFeaturedOrder');
          categoryTours = applyFeaturedOrderWithCategory(categoryTours, category.id);

          // ENFORCE: Get the first tour's first image (cover page) - this is the category cover
          if (categoryTours.length > 0) {
            const firstTour = categoryTours[0];
            // console.log(`   ✅ CategoryManagement: First tour for "${category.name}": "${firstTour.title}" (ID: ${firstTour.id})`);
            // console.log(`   📸 Tour has ${firstTour.images?.length || 0} images`);

            if (firstTour.images && firstTour.images.length > 0 && firstTour.images[0]) {
              // ENFORCE: Use the first image of the first tour as the category cover
              images[category.id] = firstTour.images[0];
              // console.log(`   ✅ Using image: ${firstTour.images[0].substring(0, 60)}...`);
            } else {
              console.warn(`   ⚠️ First tour "${firstTour.title}" has no images`);
            }
          } else {
            console.warn(`   ⚠️ No tours found for category "${category.name}"`);
          }
        } catch (error) {
          console.error(`Error loading cover image for category ${category.id}:`, error);
        }
      })
    );

    setCategoryImages(images);
  };

  // Persist form data to localStorage
  useEffect(() => {
    if (!editingCategory && formData.name && showAddForm) {
      localStorage.setItem('category_form_cache', JSON.stringify(formData));
    }
  }, [formData, editingCategory, showAddForm]);

  const clearFormCache = () => {
    localStorage.removeItem('category_form_cache');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      if (editingCategory) {
        // Update existing category
        await categoryService.updateCategory(editingCategory.id, formData);
        setSuccess('Category updated successfully');
      } else {
        // Create new category
        await categoryService.createCategory(formData);
        clearFormCache();
        setSuccess('Category created successfully');
      }

      await loadCategories();
      // Reload cover images after update
      // Force refresh after creating/updating category
      const updatedCategories = await categoryService.getAllCategories(true);
      await loadCategoryCoverImages(updatedCategories);
      resetForm();
    } catch (error: any) {
      setError(error.message || 'Failed to save category');
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      color: category.color || '#059669',
      icon: category.icon || '🏷️',
      isActive: category.isActive
    });
    setShowAddForm(true);
  };

  const handleDelete = async (category: Category) => {
    if (!window.confirm(`Are you sure you want to delete the category "${category.name}"?`)) {
      return;
    }

    try {
      await categoryService.deleteCategory(category.id);
      setSuccess('Category deleted successfully');
      await loadCategories();
    } catch (error: any) {
      setError(error.message || 'Failed to delete category');
    }
  };

  const handleToggleStatus = async (category: Category) => {
    try {
      await categoryService.toggleCategoryStatus(category.id);
      setSuccess(`Category ${category.isActive ? 'deactivated' : 'activated'} successfully`);
      await loadCategories();
    } catch (error: any) {
      setError(error.message || 'Failed to update category status');
    }
  };

  const resetForm = () => {
    if (!editingCategory && formData.name && window.confirm('Clear unsaved changes?')) {
      clearFormCache();
    }
    setFormData({
      name: '',
      description: '',
      color: '#059669',
      icon: '🏷️',
      isActive: true
    });
    setEditingCategory(null);
    setShowAddForm(false);
    setError(null);
    setSuccess(null);
  };

  const predefinedColors = [
    '#059669', '#DC2626', '#7C3AED', '#0891B2', '#EA580C',
    '#BE185D', '#0D9488', '#CA8A04', '#9333EA', '#16A34A'
  ];

  const predefinedIcons = [
    '🦁', '🏛️', '⛰️', '📸', '📷', '🌍', '🏕️', '🚁', '🎯', '🌟',
    '🏔️', '🌊', '🌅', '🌙', '🔥', '❄️', '🌈', '🎨', '🎭', '🎪'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`rounded-2xl shadow-modern backdrop-blur-sm border p-6 transition-all duration-300 ${actualTheme === 'dark'
        ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
        : 'bg-white/90 border-neutral-200/50'
        }`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className={`text-2xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
              }`}>
              Category Management
            </h2>
            <p className={`mt-1 text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
              }`}>
              Manage tour categories for organizing and filtering tours
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-modern transition-all duration-300 hover-magnify-sm modern-focus"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Category
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className={`border rounded-modern p-4 transition-colors duration-300 ${actualTheme === 'dark'
          ? 'bg-red-900/20 border-red-800 text-red-300'
          : 'bg-red-50 border-red-200 text-red-600'
          }`}>
          <div className="flex">
            <XMarkIcon className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div className={`border rounded-modern p-4 transition-colors duration-300 ${actualTheme === 'dark'
          ? 'bg-green-900/20 border-green-800 text-green-300'
          : 'bg-green-50 border-green-200 text-green-600'
          }`}>
          <div className="flex">
            <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
            <span className="text-sm">{success}</span>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className={`rounded-2xl shadow-modern backdrop-blur-sm border p-6 transition-all duration-300 ${actualTheme === 'dark'
          ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
          : 'bg-white/90 border-neutral-200/50'
          }`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
              }`}>
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </h3>
            <button
              onClick={resetForm}
              className={`p-2 rounded-modern transition-colors duration-300 ${actualTheme === 'dark'
                ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                  }`}>
                  Category Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-modern focus:ring-2 focus:outline-none transition-all duration-300 ${actualTheme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white focus:ring-primary-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                    }`}
                  placeholder="e.g., Safari, Adventure, Cultural"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                  }`}>
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-modern focus:ring-2 focus:outline-none transition-all duration-300 ${actualTheme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white focus:ring-primary-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                    }`}
                  placeholder="Brief description of the category"
                />
              </div>
            </div>

            {/* Color and Icon */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Color */}
              <div>
                <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                  }`}>
                  Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-12 h-10 border rounded-modern cursor-pointer"
                  />
                  <div className="flex flex-wrap gap-1">
                    {predefinedColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData({ ...formData, color })}
                        className="w-6 h-6 rounded border-2 border-white shadow-sm hover:scale-110 transition-transform"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Icon */}
              <div>
                <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                  }`}>
                  Icon
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className={`w-16 px-2 py-2 border rounded-modern focus:ring-2 focus:outline-none transition-all duration-300 text-center ${actualTheme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white focus:ring-primary-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-primary-500'
                      }`}
                    maxLength={2}
                  />
                  <div className="flex flex-wrap gap-1 max-w-48">
                    {predefinedIcons.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon })}
                        className="w-8 h-8 flex items-center justify-center border rounded hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Active Status */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="isActive" className={`ml-2 text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                }`}>
                Active (visible in tour creation form)
              </label>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={resetForm}
                className={`px-4 py-2 border rounded-modern transition-all duration-300 hover-magnify-sm modern-focus ${actualTheme === 'dark'
                  ? 'text-slate-300 border-slate-600 hover:text-slate-200 hover:border-slate-500'
                  : 'text-gray-700 border-gray-300 hover:text-gray-900 hover:border-gray-400'
                  }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-modern transition-all duration-300 hover-magnify-sm modern-focus"
              >
                {editingCategory ? 'Update Category' : 'Create Category'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Categories List */}
      <div className={`rounded-2xl shadow-modern backdrop-blur-sm border transition-all duration-300 ${actualTheme === 'dark'
        ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
        : 'bg-white/90 border-neutral-200/50'
        }`}>
        <div className="p-6">
          <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
            }`}>
            Categories ({categories.length})
          </h3>

          {categories.length === 0 ? (
            <div className="text-center py-8">
              <TagIcon className={`w-12 h-12 mx-auto mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-400'
                }`} />
              <p className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                }`}>
                No categories found. Create your first category to get started.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => {
                const coverImage = categoryImages[category.id];
                return (
                  <div
                    key={category.id}
                    className={`border rounded-modern transition-all duration-300 hover-magnify-sm overflow-hidden ${actualTheme === 'dark'
                      ? 'bg-slate-700/50 border-slate-600/30'
                      : 'bg-gray-50 border-gray-200'
                      }`}
                  >
                    {/* Category Cover Image */}
                    {coverImage ? (
                      <div className="relative h-32 overflow-hidden">
                        <img
                          src={coverImage}
                          alt={category.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Hide image on error, show fallback
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                        {/* Category Info Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span className="text-xl">{category.icon}</span>
                              <h4 className={`font-semibold text-white transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-white'
                                }`}>
                                {category.name}
                              </h4>
                            </div>
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleStatus(category);
                                }}
                                className={`p-1 rounded transition-colors duration-300 ${category.isActive
                                  ? 'text-green-300 hover:bg-green-900/30'
                                  : 'text-gray-300 hover:bg-gray-900/30'
                                  }`}
                                title={category.isActive ? 'Deactivate' : 'Activate'}
                              >
                                {category.isActive ? (
                                  <EyeIcon className="w-4 h-4" />
                                ) : (
                                  <EyeSlashIcon className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(category);
                                }}
                                className="p-1 text-blue-300 hover:bg-blue-900/30 rounded transition-colors duration-300"
                                title="Edit"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(category);
                                }}
                                className="p-1 text-red-300 hover:bg-red-900/30 rounded transition-colors duration-300"
                                title="Delete"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Fallback when no cover image
                      <div className={`h-32 flex items-center justify-center ${actualTheme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                        }`}>
                        <span className="text-4xl">{category.icon}</span>
                      </div>
                    )}

                    {/* Category Details */}
                    <div className="p-4">
                      {!coverImage && (
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <span className="text-2xl">{category.icon}</span>
                            <div>
                              <h4 className={`font-semibold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                                }`}>
                                {category.name}
                              </h4>
                              <div className="flex items-center space-x-2">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: category.color }}
                                />
                                <span className={`text-xs transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                                  }`}>
                                  {category.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => handleToggleStatus(category)}
                              className={`p-1 rounded transition-colors duration-300 ${category.isActive
                                ? 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/20'
                                : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                              title={category.isActive ? 'Deactivate' : 'Activate'}
                            >
                              {category.isActive ? (
                                <EyeIcon className="w-4 h-4" />
                              ) : (
                                <EyeSlashIcon className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleEdit(category)}
                              className="p-1 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded transition-colors duration-300"
                              title="Edit"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(category)}
                              className="p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors duration-300"
                              title="Delete"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}

                      {category.description && (
                        <p className={`text-sm transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                          }`}>
                          {category.description}
                        </p>
                      )}

                      {coverImage && (
                        <div className="mt-2 flex items-center space-x-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: category.color }}
                          />
                          <span className={`text-xs transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                            }`}>
                            {category.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryManagement;
